import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Motion } from '@/components/ui/motion'
import BottomNavigation from '@/components/BottomNavigation'
import { useAssetData } from '@/hooks/useAssetData'
import { useDataProvider } from '@/context/DataProviderContext'
import ErrorView from '@/components/ErrorView'
import LoadingView from '@/components/LoadingView'
import { compressImage, fileToBase64, getFileMetadata as utilGetFileMetadata } from '@/utils/fileUtils'
import { FileViewer, FileUploader, DetailItem } from '@/components/detail-page'
import type { FileCache, FileCacheItem, PageDataItem } from '@/components/detail-page/types'
import Header from '@/components/Header'
import { useUserType } from '@/hooks/useUserType'
import { filterVisibleDataItems } from '@/utils/visibility'
import { getAssetValue } from '@/utils/assetValues'

import { useTranslation } from '@/hooks/useTranslation'

// Types are now imported from @/components/detail-page/types
// 
// FILEUPLOAD CONTROL FEATURE:
// This component now supports fileSettings.allowUpload parameter for children elements.
// When fileSettings.allowUpload is set to false, the upload button will be hidden 
// for installer users, while remaining functional for authorized user types.
// Default behavior: allowUpload defaults to true if not specified.
// Example usage in data structure:
// {
//   "label": "Scheda Tecnica",
//   "fileType": "PDF",
//   "fileSettings": {
//     "allowUpload": false
//   }
// }

const DetailPage = () => {
  const { uuid, sectionId } = useParams<{ uuid: string; sectionId: string }>()
  
  console.debug(`🏁 DetailPage mounted for UUID: ${uuid}, Section: ${sectionId}`)
  
  const {
    asset: data,
    isLoading,
    error,
    project,
    uuid: resolvedUuid,
    provider,
    reloadData,
    service,
    isProjectInstaller,
    fetchingStatus
  } = useAssetData(uuid)
  
  const { getService } = useDataProvider()
  
  // Log provider changes
  React.useEffect(() => {
    console.debug(`🔧 DetailPage provider changed to: ${provider} for UUID: ${resolvedUuid}`)
  }, [provider, resolvedUuid])

  // Use service methods directly (don't destructure to avoid stale closures)
  const { t } = useTranslation()
  const { userType } = useUserType()
  // Define the file cache state with proper typing
  const [fileCache, setFileCache] = useState<FileCache>({})
  const [loadingFiles, setLoadingFiles] = useState<string[]>([]) // Tracks file IDs being loaded (metadata or content)
  const [uploadingKeys, setUploadingKeys] = useState<string[]>([]) // Tracks refIds for uploads
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})
  const loadedFiles = useRef<Set<string>>(new Set()) // Track which files we've already tried to load

  const loadFileMetadata = useCallback(
    async (fileId: string) => {
      // Create a unique key combining UUID and fileId to avoid conflicts between different assets
      const cacheKey = `${resolvedUuid}_${fileId}`
      
      // Guard: don't load if already tried to load
      if (loadedFiles.current.has(cacheKey)) {
        console.debug(`⏭️ Skipping ${fileId} for ${resolvedUuid} - already processed`)
        return
      }

      console.debug(`📥 Adding ${cacheKey} to loaded files set`)
      loadedFiles.current.add(cacheKey)

      try {
        setLoadingFiles(prev => prev.includes(fileId) ? prev : [...prev, fileId])
        console.debug(`🔍 Loading file metadata for ${fileId} with UUID ${resolvedUuid} using ${provider} provider`)
        
        // Ensure we have a valid provider and service
        if (provider === 'not-set') {
          console.warn(`⚠️ Cannot load file ${fileId} - provider not set`)
          loadedFiles.current.delete(cacheKey) // Allow retry
          return
        }
        
        // Get the current service directly to avoid closure issues
        const currentService = getService(provider)
        console.debug(`🔧 Using service for provider: ${provider}`)
        console.debug(`🔧 Service methods available:`, Object.keys(currentService))
        
        // Log the exact provider value we're using
        console.debug(`🔧 About to call getFileMetadataByUUIDAndId with provider: ${provider}`)
        
        const metadata = await currentService.getFileMetadataByUUIDAndId(resolvedUuid, fileId)
        console.debug(`✅ Got file metadata for ${fileId}:`, metadata)

        const typedMetadata = metadata as { fileName?: string; mimeType?: string }
        setFileCache(prev => ({
          ...prev,
          [fileId]: {
            id: fileId,
            name: typedMetadata.fileName || `File ${fileId}`,
            mimeType: typedMetadata.mimeType || 'application/octet-stream',
            metadata: {
              fileId,
              fileName: typedMetadata.fileName || `File ${fileId}`,
              mimeType: typedMetadata.mimeType || 'application/octet-stream',
              isImage: false,
              uploadTimestamp: new Date().toISOString()
            },
            content: prev[fileId]?.content,
            error: false
          }
        }))
      } catch (error) {
        console.error(`❌ Error loading file metadata for ${fileId}:`, error)
        
        // Remove from loaded files set so it can be retried
        const cacheKey = `${resolvedUuid}_${fileId}`
        loadedFiles.current.delete(cacheKey)
        
        setFileCache(prev => ({
          ...prev,
          [fileId]: {
            id: fileId,
            name: `Error loading file ${fileId}`,
            mimeType: 'application/octet-stream',
            metadata: {
              fileId,
              fileName: `Error loading file ${fileId}`,
              mimeType: 'application/octet-stream',
              isImage: false,
              uploadTimestamp: new Date().toISOString()
            },
            error: true
          }
        }))
      } finally {
        setLoadingFiles(prev => prev.filter(id => id !== fileId))
      }
    },
    [resolvedUuid, provider, getService]
  )

  const handleDownloadFileContent = useCallback(
    async (file: FileCacheItem) => {
      const fileId = file.id
      if (!fileId || (loadingFiles.includes(fileId) && file.content)) return

      try {
        setLoadingFiles(prev => [...prev, fileId])

        // If we don't have complete metadata, try to load it
        if (!file.metadata) {
          console.warn('Metadata not found in file cache. Loading metadata first.')
          await loadFileMetadata(fileId)
          // After loading metadata, get the latest file data from cache
          const updatedFile = fileCache[fileId]
          if (!updatedFile?.metadata) {
            throw new Error(`Metadata for ${fileId} could not be loaded.`)
          }
          // Update the file with the latest data from cache
          file = {
            ...file,
            metadata: updatedFile.metadata,
            mimeType: updatedFile.metadata.mimeType,
            name: updatedFile.metadata.fileName || file.name || `File ${fileId}`
          }
        }

        const isImage = file.mimeType?.startsWith('image/') || false

        // Only download if we don't already have the content and we have a valid uuid
        if (!file.content) {
          if (!resolvedUuid) {
            console.error('Cannot download file: No UUID available')
            return
          }

          try {
            if (provider === 'not-set' || !service) {
              console.error('Cannot download file - provider not set or service unavailable')
              return
            }
            
            const fileData = await service.downloadFileContent(resolvedUuid, fileId) as { dataUrl?: string }

            if (!fileData || !fileData.dataUrl) {
              throw new Error('Invalid file data received from server')
            }

            setFileCache(prev => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                id: fileId,
                name: file.name || `File ${fileId}`,
                mimeType: file.mimeType || 'application/octet-stream',
                metadata: file.metadata || {
                  fileId,
                  fileName: file.name || `File ${fileId}`,
                  mimeType: file.mimeType || 'application/octet-stream',
                  isImage: file.mimeType?.startsWith('image/') || false,
                  uploadTimestamp: new Date().toISOString()
                },
                content: fileData.dataUrl,
                error: false
              }
            }))

            if (!isImage) {
              // For non-image files, trigger download
              const response = await fetch(fileData.dataUrl)
              const blob = await response.blob()
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = file.name || `file-${fileId}`
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(url)
              a.remove()
            }
          } catch (err) {
            console.error(`Error downloading file ${fileId}:`, err)
            // TODO: Add error toast/notification
          }
        }
      } catch (err) {
        console.error(`Error in downloadFileContent for file ${fileId}:`, err)
        // TODO: Add error toast/notification
      } finally {
        setLoadingFiles(prev => prev.filter(id => id !== fileId))
      }
    },
    [fileCache, loadingFiles, loadFileMetadata, resolvedUuid, provider, getService]
  )

  const handlePreviewFileContent = useCallback(
    async (file: FileCacheItem): Promise<string | undefined> => {
      const fileId = file.id
      if (!fileId) return undefined
      if (file.content) return file.content
      if (!resolvedUuid || provider === 'not-set' || !service) return undefined

      try {
        setLoadingFiles(prev => (prev.includes(fileId) ? prev : [...prev, fileId]))

        let workingFile = file
        if (!workingFile.metadata) {
          await loadFileMetadata(fileId)
          const updatedFile = fileCache[fileId]
          if (updatedFile?.metadata) {
            workingFile = {
              ...workingFile,
              metadata: updatedFile.metadata,
              mimeType: updatedFile.metadata.mimeType,
              name: updatedFile.metadata.fileName || workingFile.name || `File ${fileId}`
            }
          }
        }

        const fileData = await service.downloadFileContent(resolvedUuid, fileId) as { dataUrl?: string }
        if (!fileData?.dataUrl) return undefined

        setFileCache(prev => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            id: fileId,
            name: workingFile.name || `File ${fileId}`,
            mimeType: workingFile.mimeType || 'application/octet-stream',
            metadata: workingFile.metadata || {
              fileId,
              fileName: workingFile.name || `File ${fileId}`,
              mimeType: workingFile.mimeType || 'application/octet-stream',
              isImage: workingFile.mimeType?.startsWith('image/') || false,
              uploadTimestamp: new Date().toISOString()
            },
            content: fileData.dataUrl,
            error: false
          }
        }))

        return fileData.dataUrl
      } catch (err) {
        console.error(`Error previewing file ${fileId}:`, err)
        return undefined
      } finally {
        setLoadingFiles(prev => prev.filter(id => id !== fileId))
      }
    },
    [fileCache, loadFileMetadata, resolvedUuid, provider, service]
  )

  const handleFileUpload = async (file: File, refId: string): Promise<void> => {
    if (provider === 'not-set' || !service) {
      console.error('Cannot upload file - provider not set or service unavailable')
      return
    }
    
    try {
      setUploadingKeys(prev => [...prev, refId])
      const prepared = await compressImage(file)
      const base64Data = await fileToBase64(prepared)
      const partialMetadata = utilGetFileMetadata(prepared)
      const result = await service.uploadFile(resolvedUuid, base64Data, partialMetadata) as string
      const fileIdMatch = result.match(/file-(\w+)/)
      const fileId = fileIdMatch?.[1]

      if (!fileId) {
        throw new Error('Failed to extract file ID from upload response')
      }

      const key = refId.replace('#/values/', '')
      console.log('Upload complete. Using key path:', key, 'for fileId:', fileId)
      await service.updateValue(resolvedUuid, key, "file-"+fileId, true)

      await reloadData()
      loadFileMetadata(fileId)
    } catch (err) {
      console.error('Error uploading file:', err)
      // TODO: Add error toast/notification
    } finally {
      setUploadingKeys(prev => prev.filter(key => key !== refId))
    }
  }

  // Clear loaded files tracking when UUID changes
  useEffect(() => {
    console.debug(`🔄 Clearing loaded files cache for new UUID: ${resolvedUuid}`)
    loadedFiles.current.clear()
    setFileCache({}) // Also clear the file cache when switching assets
    setLoadingFiles([]) // Clear loading state
  }, [resolvedUuid])

  // Effect to load file metadata when data changes, but only once per file
  useEffect(() => {
    // Don't start loading files until everything is fully resolved
    if (!data || !sectionId || !data.nodes[sectionId] || isLoading || provider === 'not-set' || !service || fetchingStatus !== 'completed') {
      console.debug(`⏸️ Waiting for complete resolution: data=${!!data}, sectionId=${sectionId}, isLoading=${isLoading}, provider=${provider}, service=${!!service}, fetchingStatus=${fetchingStatus}`)
      return
    }

    console.debug(`🚀 Starting file loading with resolved provider: ${provider} for UUID: ${resolvedUuid}`)

    const currentSectionData = data.nodes[sectionId]
    const fileItems =
      currentSectionData.children?.filter((item: PageDataItem) => item.fileType && item.value && item.value !== '-') ||
      []

    fileItems.forEach((item: PageDataItem) => {
      if (service.isFileId(item.value)) {
        const cacheKey = `${resolvedUuid}_${item.value}`
        if (!loadedFiles.current.has(cacheKey)) {
          console.debug(`📁 Found file to load: ${item.value} for section ${sectionId} using ${provider} provider`)
          loadFileMetadata(item.value)
        }
      }
    })
  }, [data, sectionId, service, loadFileMetadata, isLoading, provider, resolvedUuid, fetchingStatus])

  if (isLoading && !data) {
    return <LoadingView message={t('LOADING_PAGE')} />
  }

  if (error) {
    return <ErrorView message={typeof error === 'string' ? error : 'An unknown error occurred'} />
  }

  if (!data || !sectionId || !data.nodes[sectionId]) {
    return <ErrorView message={sectionId ? `Section "${sectionId}" not found` : 'Section data not available.'} />
  }

  const pageData = data.nodes[sectionId]

  // On site the installer identifies the frame by its window room, not by the
  // QR serial, so the room number leads the title whenever we know it.
  const positionNumber = getAssetValue(data, '#/values/positionInfo/number')
  const headerTitle = positionNumber
    ? `${pageData.title} - ${t('LINKING_POSITION_NUMBER')} n. ${positionNumber}`
    : `${pageData.title} - ${resolvedUuid}`

  // Filter children based on visibility rules
  const visibleChildren = pageData.children 
    ? filterVisibleDataItems(pageData.children, userType) 
    : []

  const renderValue = (item: PageDataItem) => {
    if (provider === 'not-set' || !service) {
      return <span className='text-[hsl(var(--muted-foreground))] italic text-sm'>Loading...</span>
    }
    
    if (service.isFileId(item.value)) {
      const fileId = item.value
      const fileInfo = fileCache[fileId]
      const isLoading = loadingFiles.includes(fileId)
      const isImage = fileInfo?.mimeType?.startsWith('image/') || false

      if (!fileInfo) {
        // If file info is not loaded yet, show a loading state
        return (
          <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] mb-4'>
            <div className='p-4'>
              <div className='flex items-center justify-center p-4'>
                <span className='spinner'></span>
                <span className='ml-2'>{t('LOADING_FILE_INFO')}</span>
              </div>
            </div>
          </div>
        )
      }

      return (
        <FileViewer
          key={fileId}
          file={{
            id: fileId,
            name: fileInfo.name || `File ${fileId}`,
            mimeType: fileInfo.mimeType || 'application/octet-stream',
            content: fileInfo.content,
            error: fileInfo.error,
            metadata: fileInfo.metadata || {
              fileId,
              fileName: fileInfo.name || `File ${fileId}`,
              mimeType: fileInfo.mimeType || 'application/octet-stream',
              isImage: isImage,
              uploadTimestamp: new Date().toISOString()
            }
          }}
          onDownload={handleDownloadFileContent}
          onPreview={handlePreviewFileContent}
          isDownloading={isLoading}
        />
      )
    }

    if ((item.value === '-' || item.value === '') && item.fileType) {
      // Check if file uploads are allowed for this item
      const allowUpload = item.fileSettings?.allowUpload ?? true // Default to true if not specified
      
      // Only show upload button if:
      // 1. User is a project installer
      // 2. fileSettings.allowUpload is true (or not specified)
      if (!isProjectInstaller || !allowUpload) {
        return <span className='text-[hsl(var(--muted-foreground))] italic text-sm'>{t('NO_FILE_UPLOADED')}</span>
      }

      const inputId = `file-upload-${item.label.replace(/\s+/g, '-').toLowerCase()}`
      const refId =
        item.path ||
        (sectionId
          ? `#/values/${sectionId}/${item.label.replace(/\s+/g, '_').toLowerCase()}`
          : `#/values/unknown_section/${item.label.replace(/\s+/g, '_').toLowerCase()}`)

      return (
        <FileUploader
          label={item.label}
          fileType={item.fileType}
          refId={refId}
          isUploading={uploadingKeys.includes(refId)}
          onUpload={handleFileUpload}
          inputRef={{
            current: fileInputRefs.current[inputId] || null
          }}
        />
      )
    }

    // Default rendering for other values
    return <span className='text-[hsl(var(--foreground))]'>{String(item.value)}</span>
  }

  return (
    <div className='min-h-screen bg-[hsl(var(--background))]'>
      <Header title={headerTitle} showBack />

      <div className='container mx-auto px-4 py-6 pb-24'>
        <Motion variant='fadeIn' className='space-y-6'>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            {visibleChildren.map((item: PageDataItem, index: number) => (
              <DetailItem key={index} item={item}>
                {renderValue(item)}
              </DetailItem>
            ))}
          </div>
        </Motion>
      </div>

      <BottomNavigation projectId={project?.uuid} equipmentUuid={uuid} />
    </div>
  )
}

export default DetailPage
